import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  DATASET_HEADER,
  datasetHeaders,
  getActiveDataset,
  type DatasetKind,
} from "../../data-hub-library/data/dataset";
import { get, post, put, del } from "../../Services/ApiRequest";
import type {
  RoComputeResult,
  RoConfig,
  RoConfigPayload,
  RoDimensionItem,
  RoMetricItem,
  RoWeightItem,
} from "../../Types/RoConfig";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  downloadBlob,
  filenameFromContentDisposition,
} from "../../Utils/Helper/downloadFile";
import { toast } from "../../Utils/Helper/toast";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type DataHubWeightMetric = {
  name: string;
  weight: number;
  enabled?: boolean;
  heading?: string;
  description?: string;
  cadence?: string;
};

export type DataHubWeightCategory = {
  name: string;
  weight: number;
  enabled?: boolean;
  metrics?: DataHubWeightMetric[];
};

export type DataHubWeightageVersion = {
  id: string;
  version: number;
  label?: string | null;
  is_active: boolean;
  config?: {
    categories?: DataHubWeightCategory[];
    categoryWeights?: Record<string, number>;
    metricWeights?: Record<string, number>;
    categoryEnabled?: Record<string, boolean>;
    metricEnabled?: Record<string, boolean>;
  };
  created_at?: string;
  updated_at?: string;
};

export type DataHubMetricDefinition = {
  id?: string | null;
  metric_id: string;
  metric_name: string;
  category?: string | null;
  category_id?: string | null;
  category_key?: string | null;
  full_category_name?: string | null;
  subcategory?: string | null;
  unit_of_measure?: string | null;
  format?: string | null;
  scale?: string | null;
  normalization_direction?: string | null;
  normalization_type?: string | null;
  mandatory_tier?: string | null;
  default_weight?: number | null;
  weight?: number | null;
  kpi_status?: string | null;
  export_cadence?: string | null;
  export_time_window?: string | null;
  business_impact?: string | null;
  formula?: string | null;
  source_system?: string | null;
  source_table?: string | null;
  enabled?: boolean;
  skip?: boolean;
  description?: string | null;
};

export type DataHubQaCheck = {
  id?: string;
  check_key: string;
  label: string;
  description?: string | null;
  group_name?: string | null;
  severity: string;
  enabled: boolean;
  builtin?: boolean;
};

export type DataHubQaRunCheckResult = {
  id: string;
  label: string;
  group?: string;
  status: string;
  count?: number | null;
  detail?: string;
};

export type DataHubQaRunResult = {
  id?: string;
  summary?: {
    total?: number;
    passed?: number;
    warnings?: number;
    failures?: number;
    errors?: number;
    skipped?: number;
  };
  checks?: DataHubQaRunCheckResult[];
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
  created_at?: string | null;
  status?: string;
};

export type DataHubQaRunHistoryItem = {
  id: string;
  run_no?: number;
  summary?: DataHubQaRunResult["summary"];
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
  created_at?: string | null;
  triggered_by?: string | null;
  status?: string;
};

export type DataHubRunStatus = "running" | "succeeded" | "partial" | "failed";
export type DataHubPhaseStatus = "success" | "failed" | "skipped" | "running";

export type DataHubPhaseResult = {
  phase: string;
  status: DataHubPhaseStatus | string;
  count?: number | null;
  error?: string | null;
  reason?: string | null;
  extras?: Record<string, unknown> | null;
  group?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
};

export type DataHubPipelineRun = {
  id: string;
  run_no?: number;
  status?: DataHubRunStatus | string;
  started_at?: string | null;
  finished_at?: string | null;
  created_at?: string | null;
  duration_ms?: number | null;
  source?: string | null;
  triggered_by?: string | null;
  dataset_kind?: "production" | "synthetic" | string | null;
  phases?: DataHubPhaseResult[];
};

const normalizePipelineRun = (
  raw: any,
  index: number,
  total: number
): DataHubPipelineRun => ({
  id: String(raw?.id ?? ""),
  // Newest-first list → descending display numbers (#N … #1).
  run_no: raw?.run_no ?? total - index,
  status: raw?.status ?? "running",
  started_at: raw?.started_at ?? raw?.created_at ?? null,
  finished_at: raw?.finished_at ?? null,
  created_at: raw?.created_at ?? null,
  duration_ms: raw?.duration_ms ?? null,
  source: raw?.source ?? null,
  triggered_by: raw?.triggered_by ?? null,
  dataset_kind: raw?.dataset_kind ?? "production",
  phases: Array.isArray(raw?.phases)
    ? raw.phases.map((p: any) => ({
        phase: String(p?.phase ?? ""),
        status: p?.status ?? "skipped",
        count: p?.count ?? null,
        error: p?.error ?? null,
        reason: p?.reason ?? null,
        extras: p?.extras ?? null,
        group: p?.group ?? null,
        started_at: p?.started_at ?? null,
        finished_at: p?.finished_at ?? null,
      }))
    : [],
});

export type DataHubKpiCategory = {
  id: string;
  label: string;
  long_label?: string | null;
  description?: string | null;
  weight?: number | null;
  enabled: boolean;
  sort_order?: number | null;
};

export type DataHubReportKey = "qc_file" | "qc_production" | "audit";

/** One page of a pipeline report, as the server computed it. */
export type DataHubReportPage = {
  report: string;
  title: string;
  columns: string[];
  rows: Record<string, any>[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  search: string | null;
};

export type DataHubReportQuery = {
  key: DataHubReportKey;
  runId?: string | null;
  batchId?: number | null;
  q?: string | null;
};

export type DataHubResetTarget = {
  stage: string;
  label: string;
  table: string;
};

export type DataHubResetTargets = {
  targets: DataHubResetTarget[];
  presets: Record<string, string[]>;
  organization_name: string | null;
};

export type DataHubResetResult = {
  dry_run: boolean;
  requires_confirmation: boolean;
  total_rows: number;
  total_deleted: number;
  targets: {
    stage: string;
    table: string;
    label: string;
    rows: number;
    deleted: number;
    skipped?: string;
  }[];
};

type AsyncBlock<T> = {
  data: T;
  loading: boolean;
  error: string | null;
  success: boolean;
};

type DataHubState = {
  weightage: AsyncBlock<DataHubWeightageVersion[]> & { saving: boolean };
  definitions: AsyncBlock<DataHubMetricDefinition[]> & { saving: boolean };
  categories: AsyncBlock<DataHubKpiCategory[]>;
  qa: AsyncBlock<DataHubQaCheck[]> & {
    organizationId: string | null;
    running: boolean;
    runResult: DataHubQaRunResult | null;
    selectedRunId: string | null;
    history: DataHubQaRunHistoryItem[];
    historyLoading: boolean;
  };
  reset: AsyncBlock<any> & {
    organizationId: string | null;
    preview: any;
    result: any;
  };
  pipelineRuns: AsyncBlock<DataHubPipelineRun[]> & {
    organizationId: string | null;
  };
  roConfig: AsyncBlock<RoConfig>;
  roScores: AsyncBlock<RoComputeResult | null> & {
    requestId: string | null;
  };
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const toErrorMessage = (value: any, fallback: string): string => {
  if (typeof value === "string") return value;
  if (value?.message && typeof value.message === "string") return value.message;
  if (value?.detail && typeof value.detail === "string") return value.detail;
  if (Array.isArray(value?.detail)) {
    return value.detail
      .map((item: any) =>
        typeof item === "string"
          ? item
          : item?.msg || item?.message || JSON.stringify(item)
      )
      .join(", ");
  }
  try {
    const parsed = JSON.parse(value?.message || "{}");
    if (parsed?.message) return String(parsed.message);
    if (parsed?.detail) return String(parsed.detail);
  } catch {
    /* ignore */
  }
  return (
    value?.response?.data?.message ||
    value?.response?.data?.content?.detail ||
    value?.message ||
    fallback
  );
};

const unwrapContent = (response: any) => response?.content ?? response ?? null;

const assertOk = (response: any, fallback: string) => {
  const status = response?.response?.status;
  if (typeof status === "number" && status >= 400) {
    const content = unwrapContent(response);
    throw new Error(
      response?.message || content?.detail || content?.message || fallback
    );
  }
};

const rejectError = (error: any, fallback: string) =>
  toErrorMessage(error, fallback);

/* ------------------------------------------------------------------ */
/* Weightage APIs                                                      */
/* ------------------------------------------------------------------ */

export const fetchDataHubWeightage = createAsyncThunk(
  "dataHub/fetchWeightage",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.adminMetricsWeightage());
      assertOk(response, "Failed to load weightage");
      const content = unwrapContent(response);
      return (content?.configs || []) as DataHubWeightageVersion[];
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Failed to load weightage"));
    }
  }
);

export const updateDataHubWeightage = createAsyncThunk(
  "dataHub/updateWeightage",
  async (
    payload: {
      configId: string;
      label?: string;
      config: Record<string, any>;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await put(
        API_URLS.adminMetricsWeightageItem(payload.configId),
        {
          label: payload.label,
          config: payload.config,
        }
      );
      assertOk(response, "Failed to save weightage");
      const content = unwrapContent(response);
      toast.success("Weightage saved");
      await dispatch(fetchDataHubWeightage());
      return content as DataHubWeightageVersion;
    } catch (error: any) {
      const message = rejectError(error, "Failed to save weightage");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/* ------------------------------------------------------------------ */
/* Definitions APIs                                                    */
/* ------------------------------------------------------------------ */

export const fetchDataHubDefinitions = createAsyncThunk(
  "dataHub/fetchDefinitions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.adminMetricsDefinitions());
      assertOk(response, "Failed to load definitions");
      const content = unwrapContent(response);
      return (content?.definitions || []) as DataHubMetricDefinition[];
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Failed to load definitions"));
    }
  }
);

export const createDataHubDefinition = createAsyncThunk(
  "dataHub/createDefinition",
  async (payload: Record<string, any>, { rejectWithValue, dispatch }) => {
    try {
      const response = await post(API_URLS.adminMetricsDefinitions(), payload);
      assertOk(response, "Failed to create definition");
      toast.success(`Created ${payload.metric_id || "definition"}`);
      await dispatch(fetchDataHubDefinitions());
      return unwrapContent(response);
    } catch (error: any) {
      const message = rejectError(error, "Failed to create definition");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateDataHubDefinition = createAsyncThunk(
  "dataHub/updateDefinition",
  async (
    payload: {
      metricId: string;
      data: Record<string, any>;
      silent?: boolean;
      skipRefetch?: boolean;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await put(
        API_URLS.adminMetricsDefinition(payload.metricId),
        payload.data
      );
      assertOk(response, "Failed to update definition");
      if (!payload.silent) {
        toast.success(`Updated ${payload.metricId}`);
      }
      if (!payload.skipRefetch) {
        await dispatch(fetchDataHubDefinitions());
      }
      return unwrapContent(response);
    } catch (error: any) {
      const message = rejectError(error, "Failed to update definition");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const bulkUpdateDataHubDefinitions = createAsyncThunk(
  "dataHub/bulkUpdateDefinitions",
  async (
    rows: Array<{ metricId: string; data: Record<string, any> }>,
    { rejectWithValue, dispatch }
  ) => {
    try {
      for (const row of rows) {
        const response = await put(
          API_URLS.adminMetricsDefinition(row.metricId),
          row.data
        );
        assertOk(response, `Failed to update ${row.metricId}`);
      }
      toast.success(
        `Saved ${rows.length} metric${rows.length === 1 ? "" : "s"}`
      );
      await dispatch(fetchDataHubDefinitions());
      return rows.length;
    } catch (error: any) {
      const message = rejectError(error, "Failed to save definitions");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchDataHubCategories = createAsyncThunk(
  "dataHub/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.adminMetricsCategories());
      assertOk(response, "Failed to load categories");
      const content = unwrapContent(response);
      return (content?.categories || []) as DataHubKpiCategory[];
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Failed to load categories"));
    }
  }
);

export const deleteDataHubDefinition = createAsyncThunk(
  "dataHub/deleteDefinition",
  async (metricId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await del(API_URLS.adminMetricsDefinition(metricId));
      assertOk(response, "Failed to delete definition");
      toast.success(`Deleted ${metricId}`);
      await dispatch(fetchDataHubDefinitions());
      return metricId;
    } catch (error: any) {
      const message = rejectError(error, "Failed to delete definition");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/* ------------------------------------------------------------------ */
/* QA APIs                                                             */
/* ------------------------------------------------------------------ */

export const fetchDataHubQaChecks = createAsyncThunk(
  "dataHub/fetchQaChecks",
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.adminMetricsQaChecks({ organization_id: organizationId })
      );
      assertOk(response, "Failed to load QA checks");
      const content = unwrapContent(response);
      return {
        organizationId,
        checks: (content?.checks || []) as DataHubQaCheck[],
      };
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Failed to load QA checks"));
    }
  }
);

export const updateDataHubQaCheck = createAsyncThunk(
  "dataHub/updateQaCheck",
  async (
    payload: {
      organizationId: string;
      checkKey: string;
      enabled: boolean;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await put(
        API_URLS.adminMetricsQaCheck(payload.checkKey, {
          organization_id: payload.organizationId,
        }),
        { enabled: payload.enabled }
      );
      assertOk(response, "Failed to update QA check");
      await dispatch(fetchDataHubQaChecks(payload.organizationId));
      return unwrapContent(response);
    } catch (error: any) {
      const message = rejectError(error, "Failed to update QA check");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const runDataHubQa = createAsyncThunk(
  "dataHub/runQa",
  async (organizationId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await post(API_URLS.adminMetricsQaRun(), {
        organization_id: organizationId,
        dataset_kind: getActiveDataset(),
      });
      assertOk(response, "QA run failed");
      toast.success("QA run completed");
      await dispatch(fetchDataHubQaRunHistory(organizationId));
      return unwrapContent(response) as DataHubQaRunResult;
    } catch (error: any) {
      const message = rejectError(error, "QA run failed");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchDataHubQaRunHistory = createAsyncThunk(
  "dataHub/fetchQaRunHistory",
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.adminMetricsQaRuns({
          organization_id: organizationId,
          limit: 50,
        })
      );
      assertOk(response, "Failed to load QA history");
      const content = unwrapContent(response);
      return {
        organizationId,
        runs: (content?.runs || []) as DataHubQaRunHistoryItem[],
      };
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Failed to load QA history"));
    }
  }
);

export const fetchDataHubQaRunDetail = createAsyncThunk(
  "dataHub/fetchQaRunDetail",
  async (
    payload: { organizationId: string; runId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_URLS.adminMetricsQaRunDetail(payload.runId, {
          organization_id: payload.organizationId,
        })
      );
      assertOk(response, "Failed to load QA run");
      return {
        runId: payload.runId,
        result: unwrapContent(response) as DataHubQaRunResult,
      };
    } catch (error: any) {
      const message = rejectError(error, "Failed to load QA run");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/* ------------------------------------------------------------------ */
/* Reset APIs                                                          */
/* ------------------------------------------------------------------ */

export const previewDataHubReset = createAsyncThunk(
  "dataHub/previewReset",
  async (
    payload: {
      organizationId: string;
      preset: "type_a" | "type_b";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(API_URLS.adminMetricsReset(), {
        organization_id: payload.organizationId,
        preset: payload.preset,
        dry_run: true,
        dataset_kind: getActiveDataset(),
      });
      assertOk(response, "Reset preview failed");
      return {
        organizationId: payload.organizationId,
        preview: unwrapContent(response),
      };
    } catch (error: any) {
      const message = rejectError(error, "Reset preview failed");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const executeDataHubReset = createAsyncThunk(
  "dataHub/executeReset",
  async (
    payload: {
      organizationId: string;
      preset: "type_a" | "type_b";
      confirmOrgName?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(API_URLS.adminMetricsReset(), {
        organization_id: payload.organizationId,
        preset: payload.preset,
        dry_run: false,
        confirm_org_name: payload.confirmOrgName,
        dataset_kind: getActiveDataset(),
      });
      assertOk(response, "Reset failed");
      const content = unwrapContent(response);
      toast.success(
        `Deleted ${Number(content?.total_deleted || 0).toLocaleString()} rows.`
      );
      return {
        organizationId: payload.organizationId,
        result: content,
      };
    } catch (error: any) {
      const message = rejectError(error, "Reset failed");
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/* ------------------------------------------------------------------ */
/* Pipeline runs APIs                                                  */
/* ------------------------------------------------------------------ */

export const fetchDataHubPipelineRuns = createAsyncThunk(
  "dataHub/fetchPipelineRuns",
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.adminMetricsRuns({
          organization_id: organizationId,
          dataset: getActiveDataset(),
        })
      );
      assertOk(response, "Failed to load pipeline runs");
      const content = unwrapContent(response);
      const rawRuns = Array.isArray(content?.runs) ? content.runs : [];
      return {
        organizationId,
        runs: rawRuns.map((r: any, i: number) =>
          normalizePipelineRun(r, i, rawRuns.length)
        ),
      };
    } catch (error: any) {
      return rejectWithValue(
        rejectError(error, "Failed to load pipeline runs")
      );
    }
  }
);

/* ------------------------------------------------------------------ */
/* Pipeline reports APIs                                               */
/* ------------------------------------------------------------------ */

/**
 * Paging and searching happen in SQL; rows are rendered as returned. Nothing
 * is filtered or sliced client-side, or the total would describe a different
 * set from the rows.
 */
export const fetchDataHubReport = createAsyncThunk<
  DataHubReportPage,
  DataHubReportQuery & { page?: number; pageSize?: number },
  { rejectValue: string }
>(
  "dataHub/fetchReport",
  async ({ key, runId, batchId, q, page, pageSize }, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.dataHubReport(key, {
          run_id: runId ?? null,
          batch_id: batchId ?? null,
          q: q ?? null,
          page: page ?? 1,
          page_size: pageSize ?? 50,
        }),
        datasetHeaders()
      );
      const c = unwrapContent(response) ?? {};
      return {
        report: c.report ?? key,
        title: c.title ?? key,
        columns: Array.isArray(c.columns) ? c.columns : [],
        rows: Array.isArray(c.rows) ? c.rows : [],
        total: Number(c.total ?? 0),
        page: Number(c.page ?? 1),
        page_size: Number(c.page_size ?? 50),
        pages: Number(c.pages ?? 0),
        search: c.search ?? null,
      };
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Could not load this report."));
    }
  }
);

/**
 * Downloads through the API client so the Bearer token is attached — a bare
 * `window.open` of the CSV URL fails with AUTHENTICATION_FAILED.
 */
export const downloadDataHubReportCsv = createAsyncThunk<
  void,
  DataHubReportQuery,
  { rejectValue: string }
>(
  "dataHub/downloadReportCsv",
  async ({ key, runId, batchId, q }, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.dataHubReportCsv(key, {
          run_id: runId ?? null,
          batch_id: batchId ?? null,
          q: q ?? null,
        }),
        {
          ...datasetHeaders(),
          responseType: "blob",
          returnFullResponse: true,
        }
      );
      const filename = filenameFromContentDisposition(
        response.headers?.["content-disposition"] as string | undefined,
        `${key}-report.csv`
      );
      downloadBlob(response.data as Blob, filename);
    } catch (error: any) {
      return rejectWithValue(
        rejectError(error, "Could not export this report. Try again.")
      );
    }
  }
);

/* ------------------------------------------------------------------ */
/* Stage reset APIs (Data Hub console)                                 */
/* ------------------------------------------------------------------ */

export const fetchDataHubResetTargets = createAsyncThunk<
  DataHubResetTargets,
  void,
  { rejectValue: string }
>("dataHub/fetchResetTargets", async (_, { rejectWithValue }) => {
  try {
    const response = await get(
      API_URLS.dataHubResetTargets(),
      datasetHeaders()
    );
    const c = unwrapContent(response) ?? {};
    return {
      targets: c.targets ?? [],
      presets: c.presets ?? {},
      organization_name: c.organization_name ?? null,
    };
  } catch (error: any) {
    return rejectWithValue(rejectError(error, "Failed to load reset targets."));
  }
});

/** Counts only. The server ignores dry_run here and never deletes. */
export const previewDataHubStageReset = createAsyncThunk<
  DataHubResetResult,
  { stages: Record<string, boolean>; dataset?: DatasetKind },
  { rejectValue: string }
>(
  "dataHub/previewStageReset",
  async ({ stages, dataset }, { rejectWithValue }) => {
    const kind = dataset ?? getActiveDataset();
    try {
      const response = await post(
        API_URLS.dataHubResetPreview(),
        { stages, dry_run: true, dataset: kind },
        undefined,
        { headers: { [DATASET_HEADER]: kind } }
      );
      return (unwrapContent(response) ?? {}) as DataHubResetResult;
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Reset preview failed."));
    }
  }
);

/**
 * Delete. `dry_run: false` is explicit — a body that omits it previews instead,
 * so a malformed request cannot destroy data by accident.
 */
export const runDataHubStageReset = createAsyncThunk<
  DataHubResetResult,
  {
    stages: Record<string, boolean>;
    confirmOrganizationName?: string;
    dataset?: DatasetKind;
  },
  { rejectValue: string }
>(
  "dataHub/runStageReset",
  async ({ stages, confirmOrganizationName, dataset }, { rejectWithValue }) => {
    const kind = dataset ?? getActiveDataset();
    try {
      const response = await post(
        API_URLS.dataHubReset(),
        {
          stages,
          dry_run: false,
          confirm_organization_name: confirmOrganizationName ?? null,
          dataset: kind,
        },
        undefined,
        { headers: { [DATASET_HEADER]: kind } }
      );
      return (unwrapContent(response) ?? {}) as DataHubResetResult;
    } catch (error: any) {
      return rejectWithValue(rejectError(error, "Reset failed."));
    }
  }
);

/* ------------------------------------------------------------------ */
/* R&O config APIs (local tenant rubric, not XFactor)                  */
/* ------------------------------------------------------------------ */

const EMPTY_RO_CONFIG: RoConfig = {
  riskDimensions: [],
  opportunityDimensions: [],
  metrics: [],
  dimensionWeights: [],
};

const mapRoDimension = (d: any): RoDimensionItem => ({
  key: d.key,
  label: d.label,
  category: d.category,
  included: d.included,
  sortOrder: d.sort_order,
  isRelationship: Boolean(d.is_relationship),
});

const mapRoMetric = (m: any): RoMetricItem => ({
  dimensionKey: m.dimension_key,
  dimensionLabel: m.dimension_label,
  category: m.category,
  metricId: m.metric_id,
  metricName: m.metric_name,
  included: m.included,
  sortOrder: m.sort_order,
  weightPct: m.weight_pct ?? 0,
});

const mapRoWeight = (w: any): RoWeightItem => ({
  key: w.key,
  label: w.label,
  category: w.category,
  weightPct: w.weight_pct,
  included: w.included,
});

const parseRoConfig = (raw: any): RoConfig => ({
  riskDimensions: (raw?.risk_dimensions ?? []).map(mapRoDimension),
  opportunityDimensions: (raw?.opportunity_dimensions ?? []).map(
    mapRoDimension
  ),
  metrics: (raw?.metrics ?? []).map(mapRoMetric),
  dimensionWeights: (raw?.dimension_weights ?? []).map(mapRoWeight),
});

export const fetchDataHubRoConfig = createAsyncThunk<
  RoConfig,
  void,
  { rejectValue: string }
>("dataHub/fetchRoConfig", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.dataHubRoConfig());
    return parseRoConfig(unwrapContent(response));
  } catch (error: any) {
    return rejectWithValue(rejectError(error, "Failed to load R&O config."));
  }
});

export const saveDataHubRoConfig = createAsyncThunk<
  RoConfig,
  RoConfigPayload,
  { rejectValue: string }
>("dataHub/saveRoConfig", async (payload, { rejectWithValue }) => {
  try {
    const response = await put(API_URLS.dataHubRoConfig(), payload);
    return parseRoConfig(unwrapContent(response));
  } catch (error: any) {
    return rejectWithValue(rejectError(error, "Failed to save R&O config."));
  }
});

export const resetDataHubRoConfig = createAsyncThunk<
  RoConfig,
  void,
  { rejectValue: string }
>("dataHub/resetRoConfig", async (_, { rejectWithValue }) => {
  try {
    const response = await post(API_URLS.dataHubRoConfigReset(), {});
    return parseRoConfig(unwrapContent(response));
  } catch (error: any) {
    return rejectWithValue(rejectError(error, "Failed to reset R&O config."));
  }
});

/** Live local R&O scores via POST /metrics/ro/compute?persist=false. */
export const computeDataHubRoScores = createAsyncThunk<
  RoComputeResult,
  void,
  { rejectValue: string }
>("dataHub/computeRoScores", async (_, { rejectWithValue }) => {
  try {
    // Must send X-Data-Hub-Dataset so compute uses production|synthetic
    // timeseries / account_ro_scores (same as Data Hub RoScoresTab).
    const response = await post(
      API_URLS.dataHubRoCompute({ persist: false }),
      {},
      false,
      datasetHeaders()
    );
    const content = unwrapContent(response);
    if (content?.error) return rejectWithValue(String(content.error));
    return (content ?? {}) as RoComputeResult;
  } catch (error: any) {
    return rejectWithValue(rejectError(error, "Failed to load R&O scores."));
  }
});

/* ------------------------------------------------------------------ */
/* Slice                                                               */
/* ------------------------------------------------------------------ */

const initialAsync = <T>(data: T): AsyncBlock<T> => ({
  data,
  loading: false,
  error: null,
  success: false,
});

const initialState: DataHubState = {
  weightage: { ...initialAsync<DataHubWeightageVersion[]>([]), saving: false },
  definitions: {
    ...initialAsync<DataHubMetricDefinition[]>([]),
    saving: false,
  },
  categories: initialAsync<DataHubKpiCategory[]>([]),
  qa: {
    ...initialAsync<DataHubQaCheck[]>([]),
    organizationId: null,
    running: false,
    runResult: null,
    selectedRunId: null,
    history: [],
    historyLoading: false,
  },
  reset: {
    ...initialAsync<any>(null),
    organizationId: null,
    preview: null,
    result: null,
  },
  pipelineRuns: {
    ...initialAsync<DataHubPipelineRun[]>([]),
    organizationId: null,
  },
  roConfig: initialAsync<RoConfig>(EMPTY_RO_CONFIG),
  roScores: {
    ...initialAsync<RoComputeResult | null>(null),
    requestId: null,
  },
};

const DataHubSlice = createSlice({
  name: "dataHub",
  initialState,
  reducers: {
    clearDataHubWeightageError(state) {
      state.weightage.error = null;
    },
    clearDataHubDefinitionsError(state) {
      state.definitions.error = null;
    },
    clearDataHubQaError(state) {
      state.qa.error = null;
    },
    clearDataHubQaRunResult(state) {
      state.qa.runResult = null;
      state.qa.selectedRunId = null;
    },
    selectDataHubQaRun(state, action) {
      state.qa.selectedRunId = action.payload as string | null;
    },
    clearDataHubResetState(state) {
      state.reset.error = null;
      state.reset.preview = null;
      state.reset.result = null;
      state.reset.success = false;
    },
    clearDataHubPipelineRunsError(state) {
      state.pipelineRuns.error = null;
    },
  },
  extraReducers: builder => {
    // Weightage
    builder
      .addCase(fetchDataHubWeightage.pending, state => {
        state.weightage.loading = true;
        state.weightage.error = null;
        state.weightage.success = false;
      })
      .addCase(fetchDataHubWeightage.fulfilled, (state, action) => {
        state.weightage.loading = false;
        state.weightage.success = true;
        state.weightage.data = action.payload;
      })
      .addCase(fetchDataHubWeightage.rejected, (state, action) => {
        state.weightage.loading = false;
        state.weightage.error = (action.payload as string) || "Failed to load";
        state.weightage.data = [];
      })
      .addCase(updateDataHubWeightage.pending, state => {
        state.weightage.saving = true;
        state.weightage.error = null;
      })
      .addCase(updateDataHubWeightage.fulfilled, state => {
        state.weightage.saving = false;
        state.weightage.success = true;
      })
      .addCase(updateDataHubWeightage.rejected, (state, action) => {
        state.weightage.saving = false;
        state.weightage.error = (action.payload as string) || "Failed to save";
      });

    // Definitions
    builder
      .addCase(fetchDataHubDefinitions.pending, state => {
        state.definitions.loading = true;
        state.definitions.error = null;
        state.definitions.success = false;
      })
      .addCase(fetchDataHubDefinitions.fulfilled, (state, action) => {
        state.definitions.loading = false;
        state.definitions.success = true;
        state.definitions.data = action.payload;
      })
      .addCase(fetchDataHubDefinitions.rejected, (state, action) => {
        state.definitions.loading = false;
        state.definitions.error =
          (action.payload as string) || "Failed to load";
        state.definitions.data = [];
      })
      .addCase(createDataHubDefinition.pending, state => {
        state.definitions.saving = true;
        state.definitions.error = null;
      })
      .addCase(createDataHubDefinition.fulfilled, state => {
        state.definitions.saving = false;
        state.definitions.success = true;
      })
      .addCase(createDataHubDefinition.rejected, (state, action) => {
        state.definitions.saving = false;
        state.definitions.error =
          (action.payload as string) || "Failed to create";
      })
      .addCase(updateDataHubDefinition.pending, state => {
        state.definitions.saving = true;
        state.definitions.error = null;
      })
      .addCase(updateDataHubDefinition.fulfilled, state => {
        state.definitions.saving = false;
        state.definitions.success = true;
      })
      .addCase(updateDataHubDefinition.rejected, (state, action) => {
        state.definitions.saving = false;
        state.definitions.error =
          (action.payload as string) || "Failed to update";
      })
      .addCase(bulkUpdateDataHubDefinitions.pending, state => {
        state.definitions.saving = true;
        state.definitions.error = null;
      })
      .addCase(bulkUpdateDataHubDefinitions.fulfilled, state => {
        state.definitions.saving = false;
        state.definitions.success = true;
      })
      .addCase(bulkUpdateDataHubDefinitions.rejected, (state, action) => {
        state.definitions.saving = false;
        state.definitions.error =
          (action.payload as string) || "Failed to save";
      })
      .addCase(deleteDataHubDefinition.pending, state => {
        state.definitions.saving = true;
        state.definitions.error = null;
      })
      .addCase(deleteDataHubDefinition.fulfilled, state => {
        state.definitions.saving = false;
        state.definitions.success = true;
      })
      .addCase(deleteDataHubDefinition.rejected, (state, action) => {
        state.definitions.saving = false;
        state.definitions.error =
          (action.payload as string) || "Failed to delete";
      });

    // Categories
    builder
      .addCase(fetchDataHubCategories.pending, state => {
        state.categories.loading = true;
        state.categories.error = null;
        state.categories.success = false;
      })
      .addCase(fetchDataHubCategories.fulfilled, (state, action) => {
        state.categories.loading = false;
        state.categories.success = true;
        state.categories.data = action.payload;
      })
      .addCase(fetchDataHubCategories.rejected, (state, action) => {
        state.categories.loading = false;
        state.categories.error = (action.payload as string) || "Failed to load";
        state.categories.data = [];
      });

    // QA
    builder
      .addCase(fetchDataHubQaChecks.pending, state => {
        state.qa.loading = true;
        state.qa.error = null;
        state.qa.success = false;
      })
      .addCase(fetchDataHubQaChecks.fulfilled, (state, action) => {
        state.qa.loading = false;
        state.qa.success = true;
        state.qa.organizationId = action.payload.organizationId;
        state.qa.data = action.payload.checks;
      })
      .addCase(fetchDataHubQaChecks.rejected, (state, action) => {
        state.qa.loading = false;
        state.qa.error = (action.payload as string) || "Failed to load";
        state.qa.data = [];
      })
      .addCase(runDataHubQa.pending, state => {
        state.qa.running = true;
        state.qa.error = null;
      })
      .addCase(runDataHubQa.fulfilled, (state, action) => {
        state.qa.running = false;
        state.qa.runResult = (action.payload ||
          null) as DataHubQaRunResult | null;
        state.qa.selectedRunId = action.payload?.id || null;
      })
      .addCase(runDataHubQa.rejected, (state, action) => {
        state.qa.running = false;
        state.qa.error = (action.payload as string) || "QA run failed";
      })
      .addCase(fetchDataHubQaRunHistory.pending, state => {
        state.qa.historyLoading = true;
        state.qa.history = [];
      })
      .addCase(fetchDataHubQaRunHistory.fulfilled, (state, action) => {
        state.qa.historyLoading = false;
        state.qa.history = action.payload.runs;
      })
      .addCase(fetchDataHubQaRunHistory.rejected, (state, action) => {
        state.qa.historyLoading = false;
        state.qa.history = [];
        state.qa.error =
          (action.payload as string) ||
          state.qa.error ||
          "Failed to load history";
      })
      .addCase(fetchDataHubQaRunDetail.pending, state => {
        state.qa.error = null;
      })
      .addCase(fetchDataHubQaRunDetail.fulfilled, (state, action) => {
        state.qa.selectedRunId = action.payload.runId;
        state.qa.runResult = action.payload.result;
      })
      .addCase(fetchDataHubQaRunDetail.rejected, (state, action) => {
        state.qa.error = (action.payload as string) || "Failed to load QA run";
      });

    // Reset
    builder
      .addCase(previewDataHubReset.pending, state => {
        state.reset.loading = true;
        state.reset.error = null;
        state.reset.result = null;
      })
      .addCase(previewDataHubReset.fulfilled, (state, action) => {
        state.reset.loading = false;
        state.reset.organizationId = action.payload.organizationId;
        state.reset.preview = action.payload.preview;
      })
      .addCase(previewDataHubReset.rejected, (state, action) => {
        state.reset.loading = false;
        state.reset.error = (action.payload as string) || "Preview failed";
      })
      .addCase(executeDataHubReset.pending, state => {
        state.reset.loading = true;
        state.reset.error = null;
      })
      .addCase(executeDataHubReset.fulfilled, (state, action) => {
        state.reset.loading = false;
        state.reset.success = true;
        state.reset.organizationId = action.payload.organizationId;
        state.reset.result = action.payload.result;
        state.reset.preview = null;
      })
      .addCase(executeDataHubReset.rejected, (state, action) => {
        state.reset.loading = false;
        state.reset.error = (action.payload as string) || "Reset failed";
      });

    // Pipeline runs
    builder
      .addCase(fetchDataHubPipelineRuns.pending, state => {
        state.pipelineRuns.loading = true;
        state.pipelineRuns.error = null;
        state.pipelineRuns.success = false;
      })
      .addCase(fetchDataHubPipelineRuns.fulfilled, (state, action) => {
        state.pipelineRuns.loading = false;
        state.pipelineRuns.success = true;
        state.pipelineRuns.organizationId = action.payload.organizationId;
        state.pipelineRuns.data = action.payload.runs;
      })
      .addCase(fetchDataHubPipelineRuns.rejected, (state, action) => {
        state.pipelineRuns.loading = false;
        state.pipelineRuns.error =
          (action.payload as string) || "Failed to load";
        state.pipelineRuns.data = [];
      });

    // R&O config
    builder
      .addCase(fetchDataHubRoConfig.pending, state => {
        state.roConfig.loading = true;
        state.roConfig.error = null;
      })
      .addCase(fetchDataHubRoConfig.fulfilled, (state, action) => {
        state.roConfig.loading = false;
        state.roConfig.success = true;
        state.roConfig.data = action.payload;
      })
      .addCase(fetchDataHubRoConfig.rejected, (state, action) => {
        state.roConfig.loading = false;
        state.roConfig.error = action.payload || "Failed to load R&O config.";
      })
      .addCase(saveDataHubRoConfig.fulfilled, (state, action) => {
        state.roConfig.data = action.payload;
      })
      .addCase(resetDataHubRoConfig.fulfilled, (state, action) => {
        state.roConfig.data = action.payload;
      });

    // R&O scores
    builder
      .addCase(computeDataHubRoScores.pending, (state, action) => {
        state.roScores.loading = true;
        state.roScores.error = null;
        state.roScores.requestId = action.meta.requestId;
      })
      .addCase(computeDataHubRoScores.fulfilled, (state, action) => {
        if (state.roScores.requestId !== action.meta.requestId) return;
        state.roScores.loading = false;
        state.roScores.success = true;
        state.roScores.data = action.payload;
      })
      .addCase(computeDataHubRoScores.rejected, (state, action) => {
        if (state.roScores.requestId !== action.meta.requestId) return;
        state.roScores.loading = false;
        state.roScores.data = null;
        state.roScores.error = action.payload || "Failed to load R&O scores.";
      });
  },
});

export const {
  clearDataHubWeightageError,
  clearDataHubDefinitionsError,
  clearDataHubQaError,
  clearDataHubQaRunResult,
  selectDataHubQaRun,
  clearDataHubResetState,
  clearDataHubPipelineRunsError,
} = DataHubSlice.actions;

export default DataHubSlice.reducer;
